import { Text, View, Page, Document, StyleSheet, Image } from '@react-pdf/renderer';
import { images } from '~/assets';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { DATE_FORMAT } from '~/utils/constants/common';

export const DocumentCourseReportPDF = ({ data }: { data: any }) => {
  const { student, courses } = data;
  const { t } = useTranslation(['common/course-details-report']);

  const documentTypeMap: Record<string, string> = {
    Video: t('document_type_video'),
    Word: t('document_type_word'),
    PDF: t('document_type_pdf'),
    PPT: t('document_type_ppt'),
    Excel: t('document_type_excel'),
    Image: t('document_type_image'),
  };

  const styles = StyleSheet.create({
    page: {
      fontSize: 11,
      paddingTop: 20,
      paddingLeft: 40,
      paddingRight: 40,
      paddingBottom: 40,
      lineHeight: 1.5,
      flexDirection: 'column',
    },
    container: { flexDirection: 'column', flex: 1 },
    logo: { width: 200 },
    line: { borderTopWidth: 5, borderTopColor: '#2b7abc', marginVertical: 24 },
    title: {
      fontSize: 20,
      fontWeight: 'bold',
      color: 'black',
      textAlign: 'center',
      marginBottom: 20,
    },
    table: {
      width: '100%',
      marginBottom: 16,
    },
    tableRow: { flexDirection: 'row' },
    tableCellLabel: {
      width: 180,
      padding: 5,
      color: 'black',
      fontWeight: 'bold',
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderColor: 'black',
      borderStyle: 'solid',
      textAlign: 'left',
      backgroundColor: '#fff',
    },
    tableCell: {
      flex: 1,
      padding: 5,
      color: 'black',
      fontWeight: 'normal',
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderColor: 'black',
      borderStyle: 'solid',
      textAlign: 'left',
      backgroundColor: '#fff',
    },
    tableHeader: {
      flex: 1,
      padding: 5,
      fontWeight: 'bold',
      color: 'black',
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderColor: 'black',
      borderStyle: 'solid',
      textAlign: 'left',
      backgroundColor: '#fff',
    },
    date: { textAlign: 'right', marginTop: 24, color: 'black' },
  });

  const mainCourse = courses?.[0] ?? {};
  const modules = mainCourse.modules ?? [];

  const sumConnectionTime = (times: { start: string; end: string }[]) => {
    let total = 0;
    times.forEach(({ start, end }) => {
      if (start && end) {
        total += moment(end).diff(moment(start));
      }
    });
    return total;
  };

  const formatDuration = (ms: number) => {
    const totalMinutes = Math.ceil(ms / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    let result = [];
    if (days) {
      result.push(
        days > 1
          ? t('days_plural', { count: days })
          : t('days', { count: days })
      );
    }
    if (hours) {
      result.push(
        hours > 1
          ? t('hours_plural', { count: hours })
          : t('hours', { count: hours })
      );
    }
    if (minutes || result.length === 0) {
      result.push(
        minutes > 1
          ? t('minutes_plural', { count: minutes })
          : t('minutes', { count: minutes })
      );
    }
    return result.join(', ');    
  };

  const studentConnectionTimes = [
    { start: student?.loginAt, end: student?.lastLogin }
  ].filter((item: { start: string; end: string }) => item.start && item.end);
  const totalConnectionTimeMs = sumConnectionTime(studentConnectionTimes);

  const moduleConnectionTimes = modules
    .map((mod: any) => ({
      start: mod.startConnectAt,
      end: mod.endConnectAt
    }))
    .filter((item: { start: string; end: string }) => item.start && item.end);
  const totalLessonConnectionTimeMs = sumConnectionTime(moduleConnectionTimes);


  return (
    <Document>
      <Page size='A4' style={styles.page} wrap>
        <View style={styles.container}>
          <Image style={styles.logo} source={images.commonLogoFullPositive} />
          <View style={styles.line} />
          <Text style={styles.title}>{t('report_title')}</Text>

          {/* USER INFORMATION TABLE */}
          <View style={styles.table}>
            <View style={styles.tableRow} wrap={false}>
              <View style={[styles.tableCellLabel, { borderLeftWidth: 1, borderTopWidth: 1 }]}>
                <Text>{t('user_information')}</Text>
              </View>
              <View style={[styles.tableCell, { borderTopWidth: 1 }]}>
                <Text></Text>
              </View>
            </View>
            <View style={styles.tableRow} wrap={false}>
              <View style={[styles.tableCellLabel, { borderLeftWidth: 1 }]}>
                <Text>{t('student_id')}</Text>
              </View>
              <View style={styles.tableCell}>
                <Text>{student?.id}</Text>
              </View>
            </View>
            <View style={styles.tableRow} wrap={false}>
              <View style={[styles.tableCellLabel, { borderLeftWidth: 1 }]}>
                <Text>{t('student_name')}</Text>
              </View>
              <View style={styles.tableCell}>
                <Text>{student?.name}</Text>
              </View>
            </View>
            <View style={styles.tableRow} wrap={false}>
              <View style={[styles.tableCellLabel, { borderLeftWidth: 1 }]}>
                <Text>{t('type')}</Text>
              </View>
              <View style={styles.tableCell}>
                <Text>{t('student_type')}</Text>
              </View>
            </View>
            <View style={styles.tableRow} wrap={false}>
              <View style={[styles.tableCellLabel, { borderLeftWidth: 1 }]}>
                <Text>{t('training_plan_id')}</Text>
              </View>
              <View style={styles.tableCell}>
                <Text>{mainCourse?.sisCourseId}</Text>
              </View>
            </View>
            <View style={styles.tableRow} wrap={false}>
              <View style={[styles.tableCellLabel, { borderLeftWidth: 1 }]}>
                <Text>{t('training_plan_title')}</Text>
              </View>
              <View style={styles.tableCell}>
                <Text>{mainCourse?.name}</Text>
              </View>
            </View>
            <View style={styles.tableRow} wrap={false}>
              <View style={[styles.tableCellLabel, { borderLeftWidth: 1 }]}>
                <Text>{t('project_edition_id')}</Text>
              </View>
              <View style={styles.tableCell}>
                <Text>{mainCourse?.courseCode}</Text>
              </View>
            </View>
            <View style={styles.tableRow} wrap={false}>
              <View style={[styles.tableCellLabel, { borderLeftWidth: 1 }]}>
                <Text>{t('total_connection_time')}</Text>
              </View>
              <View style={styles.tableCell}>
                <Text>{formatDuration(totalConnectionTimeMs)}</Text>
              </View>
            </View>
            <View style={styles.tableRow} wrap={false}>
              <View style={[styles.tableCellLabel, { borderLeftWidth: 1 }]}>
                <Text>{t('total_lesson_connection_time')}</Text>
              </View>
              <View style={styles.tableCell}>
                <Text>{formatDuration(totalLessonConnectionTimeMs)}</Text>
              </View>
            </View>
          </View>

          {/* MODULE TABLE */}
          <View style={styles.table}>
            <View style={styles.tableRow} wrap={false}>
              <View style={[styles.tableHeader, { borderLeftWidth: 1, borderTopWidth: 1 }]}>
                <Text>{t('module')}</Text>
              </View>
              <View style={[styles.tableHeader, { borderTopWidth: 1 }]}>
                <Text>{t('training_session_date')}</Text>
              </View>
              <View style={[styles.tableHeader, { borderTopWidth: 1 }]}>
                <Text>{t('training_session_time')}</Text>
              </View>
              <View style={[styles.tableHeader, { borderTopWidth: 1 }]}>
                <Text>{t('training_material')}</Text>
              </View>
              <View style={[styles.tableHeader, { borderTopWidth: 1 }]}>
                <Text>{t('connection_start_time')}</Text>
              </View>
              <View style={[styles.tableHeader, { borderTopWidth: 1 }]}>
                <Text>{t('connection_end_time')}</Text>
              </View>
            </View>
            {modules.map((mod: any, idx: number) => (
              <View style={styles.tableRow} key={mod.id || idx} wrap={false}>
                <View style={[styles.tableCell, { borderLeftWidth: 1 }]}>
                  <Text>{mod.name}</Text>
                </View>
                <View style={styles.tableCell}>
                  <Text>{mod.unlockAt ? moment(mod.unlockAt).format(DATE_FORMAT.DATE) : ''}</Text>
                </View>
                <View style={styles.tableCell}>
                  <Text>{mod.unlockAt ? moment(mod.unlockAt).format(DATE_FORMAT.TIME) : ''}</Text>
                </View>
                <View style={styles.tableCell}>
                  <Text>
                    {Array.isArray(mod.documentTypes)
                      ? mod.documentTypes.map((type: string) => documentTypeMap[type] || type).join(', ')
                      : ''}
                  </Text>
                </View>
                <View style={styles.tableCell}>
                  <Text>{mod.startConnectAt ? moment(mod.startConnectAt).format(DATE_FORMAT.PDF_DATE) : ''}</Text>
                </View>
                <View style={styles.tableCell}>
                  <Text>{mod.endConnectAt ? moment(mod.endConnectAt).format(DATE_FORMAT.PDF_DATE) : ''}</Text>
                </View>
              </View>
            ))}
          </View>

          <Text style={styles.date}>
            {t('date_label')} {moment().format(DATE_FORMAT.DATE)}
          </Text>
        </View>
      </Page>
    </Document>
  );
};
